import { appendSearchParams } from '@alexis/helpers/searchParam';
import sha256 from 'crypto-js/sha256';
import { BehaviorSubject, of, merge, share } from 'rxjs';
import type { Observable } from 'rxjs';
import { fromFetch } from 'rxjs/fetch';
import { switchMap, concatMap, filter, delay } from 'rxjs/operators';

import type { User } from '@wego/alexis/types/helpers/user';

import { getGenzoIdentifierId } from '@helpers/genzoIdentifierId';

import { GuestRoom } from '@wegoTypes/hotels/guestRoom';
import { HotelSearchResult } from '@wegoTypes/hotels/metasearch/hotelSearchResult';

export function hotelSearchObservable({
  url,
  search,
  similarHotelsLimit,
  user,
  selectedHotelIds,
  clientId,
  sessionId,
  extraSearchBodyParams,
  extraBodyParams,
  isGoogleHotel,
}: {
  url: string;
  search: {
    checkIn: string;
    checkOut: string;
    currencyCode: string;
    cityCode?: string;
    districtId?: string;
    regionId?: string;
    hotelId?: number;
    latitude?: number;
    longitude?: number;
    radius?: number;
    siteCode: string;
    locale: string;
    rooms: GuestRoom[];
  };
  similarHotelsLimit?: number;
  user: User | null;
  selectedHotelIds?: Array<number>;
  clientId: string | undefined;
  sessionId: string | undefined;
  extraSearchBodyParams: {
    [key: string]: string | { [key: string]: string } | boolean;
  };
  extraBodyParams: { [key: string]: string | boolean } | undefined;
  isGoogleHotel?: boolean;
}): Observable<HotelSearchResult> {
  const amountType = 'NIGHTLY';

  const body = {
    search: {
      ...search,
      appType: 'WEB_APP',
      deviceType: 'DESKTOP',
      clientCreatedAt: new Date(),
      userLoggedIn: !!user,
      ...extraSearchBodyParams,
    },
    ...(similarHotelsLimit && {
      similarHotels: {
        limit: similarHotelsLimit,
      },
    }),
    includeDirect: true, // TODO: Remove after testing!
    ...(!!selectedHotelIds && selectedHotelIds.length > 0 && { selectedHotelIds }),
    ...(!!extraBodyParams && extraBodyParams),
  };

  const searchObservable = hotelSearchFromFetchObservable(
    url,
    search.locale,
    search.currencyCode,
    amountType,
    clientId,
    sessionId,
    body,
    user,
  );

  if (search.hotelId) {
    return searchObservable;
  }
  // Polling of hotel result
  return hotelSearchResultObservable(
    searchObservable,
    url,
    search.locale,
    search.currencyCode,
    amountType,
    selectedHotelIds!,
    clientId,
    sessionId,
    isGoogleHotel,
  );
}

function hotelSearchResultObservable(
  hotelSearchIdObservable: Observable<HotelSearchResult>,
  url: string,
  locale: string,
  currencyCode: string,
  amountType: string,
  selectedHotelIds: Array<number>,
  wegoAnalyticsClientId: string | undefined,
  wegoAnalyticsSessionId: string | undefined,
  isGoogleHotel?: boolean,
): Observable<HotelSearchResult> {
  const MAX_POLLING_COUNT = isGoogleHotel ? 20 : 10;
  const hotelSearchResultObservable = hotelSearchIdObservable.pipe(
    concatMap((hotelSearchIdResultResponse: HotelSearchResult) => {
      const subject = new BehaviorSubject<{ offset: number; isLastPolling: boolean }>({
        offset: 0,
        isLastPolling: false,
      });

      return subject.pipe(
        concatMap((value: { offset: number; isLastPolling: boolean }, index: number) => {
          return fromFetch(
            appendSearchParams(`${url}/${hotelSearchIdResultResponse.search.id}/results`, {
              locale,
              currencyCode,
              amountType,
              clientId: wegoAnalyticsClientId,
              offset: value.offset,
              isLastPolling: value.isLastPolling,
              moreRates: true,
              selectedHotelIds,
            }),
            {
              method: 'GET',
              credentials: 'include',
              mode: 'cors',
              headers: {
                'Content-Type': 'application/json',
                ...(!!getGenzoIdentifierId() && {
                  'X-Wego-Genzo-Identifier-Id': getGenzoIdentifierId()!,
                }),
                'X-Wego-Client-Id': wegoAnalyticsClientId!,
                'X-Wego-Session-Id': wegoAnalyticsSessionId!,
              },
            },
          ).pipe(
            concatMap<Response, Promise<HotelSearchResult>>(async (response: Response) => {
              if (response.ok) {
                const hotelSearchResult = (await response.json()) as HotelSearchResult;

                if (index < MAX_POLLING_COUNT) {
                  of('')
                    .pipe(delay((index + 1) * 300))
                    .subscribe(() =>
                      subject.next({
                        offset: hotelSearchResult.count,
                        isLastPolling: index === MAX_POLLING_COUNT - 1,
                      }),
                    );
                } else {
                  subject.complete();
                }
                return hotelSearchResult;
              }
              throw new Error(response.status.toString());
            }),
            filter(({ rates, hotels }: HotelSearchResult) => rates.length > 0 || hotels.length > 0),
            share({
              resetOnError: false,
              resetOnComplete: false,
              resetOnRefCountZero: false,
            }),
          );
        }),
      );
    }),
  );

  return merge(hotelSearchIdObservable, hotelSearchResultObservable);
}

function hotelSearchFromFetchObservable(
  url: string,
  locale: string,
  currencyCode: string,
  amountType: string,
  wegoAnalyticsClientId: string | undefined,
  wegoAnalyticsSessionId: string | undefined,
  body: any,
  user: User | null,
): Observable<HotelSearchResult> {
  return fromFetch(
    appendSearchParams(url, { locale, currencyCode, amountType, clientId: wegoAnalyticsClientId }),
    {
      method: 'POST',
      credentials: 'include',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        ...(!!getGenzoIdentifierId() && {
          'X-Wego-Genzo-Identifier-Id': getGenzoIdentifierId()!,
        }),
        'X-Wego-Client-Id': wegoAnalyticsClientId!,
        'X-Wego-Session-Id': wegoAnalyticsSessionId!,
        ...(!!user?.email && { 'X-Wego-Hashed-User-Email': sha256(user.email).toString() }),
      },
      body: JSON.stringify(body),
    },
  ).pipe(
    switchMap<Response, Promise<HotelSearchResult>>(async (response: Response) => {
      if (response.ok) {
        return (await response.json()) as HotelSearchResult;
      }

      throw new Error(response.status.toString());
    }),
    share({
      resetOnError: false,
      resetOnComplete: false,
      resetOnRefCountZero: false,
    }),
  );
}
